<template>
  <div v-loading="loading">
    <div class="scale-template-form">
      <el-form
        size="mini"
        label-position="left"
        label-width="200px"
      >
        <el-form-item label="Название" required>
          <div class="scale-template-name-row">
            <click-to-edit
              class="scale-template-field"
              v-model="template.name"
              :disabled="updateDisabled"
              placeholder="Без названия"
              @input="updateScaleTemplate(true)"
            ></click-to-edit>
            <div class="scale-template-id">ID: {{template.id}}</div>
          </div>
        </el-form-item>

        <el-form-item label="Описание">
          <click-to-edit
            class="scale-template-field"
            v-model="template.description"
            :disabled="updateDisabled"
            style="overflow-wrap: break-word;"
            type="textarea"
            @input="updateScaleTemplate()"
          ></click-to-edit>
        </el-form-item>

        <el-form-item label="Тип шкалы" required>
          <el-select
            v-model="template.type"
            :disabled="updateDisabled"
            size="mini"
            class="scale-template-field"
            @change="updateScaleTemplate()"
          >
            <el-option
              v-for="(scaleTypeConfig, scaleKey) in $companyConfiguration.scales.getConfigurableScaleConfig().types"
              :key="scaleKey"
              :value="scaleKey"
              :label="scaleTypeConfig.showName"
            ></el-option>
          </el-select>
        </el-form-item>

        <!-- v-show висит на свитче и его подписи, а не на всём form-item: группа стоит в этой же
             строке по макету, но от критериев не зависит и должна оставаться доступной у типов
             без scopes (например, «Ручная оценка» у wilo) -->
        <el-form-item label="Разрешить редактировать критерии в целях">
          <template v-slot:label>
            <span v-show="scalesScopesTemplate">
            Разрешить редактировать критерии в целях
            <el-popover
              style=""
              placement="top"
              width="400"
              trigger="click"
            >
              <el-button
                slot="reference"
                class="table-action-button danger"
                type="text"
                round
                size="mini"
                icon="fas fa-info-circle"
              ></el-button>

              <div style="white-space: pre-line">У показателя будут самостоятельные критерии, которые пользователь сможет
                редактировать по своему усмотрению. Критерии из библиотеки будут добавлены для примера.
              </div>
              <br>
              <div style="white-space: pre-line">Изменение данного параметра ничего не меняет в уже настроенных
                показателях. Просто в следующем выборе факта будут учитываться вручную настроенные критерии
              </div>
            </el-popover>
            </span>
          </template>

          <!-- Свитч и выбор группы в одной строке — как в макете -->
          <div class="scale-template-switch-row">
            <el-switch
              v-show="scalesScopesTemplate"
              v-model="template.allow_scopes_edit_in_element"
              :disabled="updateDisabled"
              size="mini"
              @change="updateScaleTemplate()"
            >
            </el-switch>

            <div class="scale-template-group">
              <span class="scale-template-group-label">Группа</span>
              <el-select
                v-model="template.scale_group_id"
                :disabled="updateDisabled"
                size="mini"
                class="scale-template-group-select"
                @change="updateScaleTemplate(true)"
              >
                <el-option
                  v-for="group in groups"
                  :key="group.id"
                  :value="group.id"
                  :label="group.name"
                ></el-option>
              </el-select>
            </div>
          </div>
        </el-form-item>
      </el-form>
    </div>

    <component
      :is="scalesScopesTemplate"
      :scale="template"
      :disabled="updateDisabled"
      @add-scope="saveScope({})"
      @save-scope="saveScope"
      @delete-scope="deleteScope"
    ></component>

    <div class="scale-template-footer">
      <el-button
        v-if="!updateDisabled"
        size="mini"
        icon="fas fa-sync"
        @click="recountRelatedElements"
      >
        Пересчитать цели в активных картах
      </el-button>

      <div class="scale-template-footer-right">
        <el-switch
          v-model="template.disabled"
          :disabled="updateDisabled"
          :active-value="false"
          :inactive-value="true"
          active-text="Активная"
          @change="updateScaleTemplate()"
        >
        </el-switch>

        <el-popconfirm
          v-if="$canAndRulesPass(template, $permissions.CONFIGURABLE_SCALES.DELETE)"
          confirm-button-text='Да'
          cancel-button-text='нет'
          icon="el-icon-question"
          :title="deleteConfirmTitle"
          @confirm="deleteScaleTemplate"
        >
          <el-button
            slot="reference"
            size="mini"
            plain
            icon="far fa-trash-alt"
            :loading="loading"
          >
            Удалить
          </el-button>
        </el-popconfirm>
      </div>
    </div>
  </div>
</template>

<script>
import requestSender from "@/api/base/requestSender";
import ClickToEdit from "@/components/ClickToEdit.vue";
import {mapGetters} from "vuex";

export default {
  name: "scale-template",
  components: {ClickToEdit},
  props: {
    template: {type: Object, required: true},
    // Родитель уже держит группы из grouped-templates-list — свой запрос тут был бы
    // лишним на каждый клик по шкале (компонент ремаунтится по :key)
    groups: {type: Array, default: () => []},
  },
  watch: {},
  computed: {
    ...mapGetters(['user']),
    scalesScopesTemplate() {
      return this.$companyConfiguration.scales.getConfigurableScaleSettingsTemplate(this.template.type);
    },
    updateDisabled(){
      return !this.$canAndRulesPass(this.template, this.$permissions.CONFIGURABLE_SCALES.UPDATE);
    },
    deleteConfirmTitle() {
      if (this.template.used_in_cards) {
        return 'Шкала используется в существующих картах. Значения в них сохранятся, но шкала станет недоступна для новых целей. Удалить?';
      }
      return 'Вы уверены, что хотите удалить шкалу?';
    },
  },
  data() {
    return {
      loading: false,
    }
  },
  methods: {

    updateScaleTemplate(rebuildTree = false) {
      this.loading = true;
      requestSender('post', 'scale/update', this.template)
        .then(data => {
          this.$set(this.template, 'scopes', data.scale.scopes)
          this.$emit('update:template', data.scale, rebuildTree === true);
        })
        .finally(() => {
          this.loading = false;
        })
    },
    deleteScaleTemplate() {
      this.loading = true;
      requestSender('post', 'scale/delete', {
        id: this.template.id,
      })
        .then(data => {
          this.$emit('template-deleted', data.template);
        })
        .finally(() => {
          this.loading = false;
        })
    },

    saveScope(scope) {
      this.loading = true;
      requestSender('post', 'scale/save-scale-scope', {
        scale_id: this.template.id,
        scope_id: scope.id,
        data: scope,
      })
        .then(data => {
          this.$set(this.template, 'scopes', data.scale.scopes);
        })
        .finally(() => {
          this.loading = false;
        })
    },
    deleteScope(scope) {
      this.loading = true;
      requestSender('post', 'scale/delete-scale-scope', {
        scale_id: this.template.id,
        scope_id: scope.id,
      })
        .then(data => {
          this.$set(this.template, 'scopes', data.scale.scopes);
        })
        .finally(() => {
          this.loading = false;
        })
    },

    recountRelatedElements() {
      this.loading = true;
      requestSender('post', 'scale/recount-related-elements', {
        scale_id: this.template.id,
      })
        .finally(() => {
          this.loading = false;
        })
    },
  }
}
</script>


<style lang="scss" scoped>
.scale-template-form {
  ::v-deep .el-form-item__label {
    color: #323232;
    line-height: 1.3;
    padding-right: 20px;
  }
}

.scale-template-name-row {
  display: flex;
  align-items: center;
  gap: 30px;
}

.scale-template-id {
  color: #909399;
  white-space: nowrap;
}

// Поля формы визуально выровнены в общую колонку
.scale-template-field {
  width: 100%;
}

.scale-template-switch-row {
  display: flex;
  align-items: center;
  gap: 20px;
}

.scale-template-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.scale-template-group-label {
  color: #606266;
  white-space: nowrap;
}

.scale-template-group-select {
  flex: 1;
  max-width: 300px;
  min-width: 0;
}

.scale-template-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #E9E9E9;
}

.scale-template-footer-right {
  display: flex;
  align-items: center;
  gap: 15px;
}
</style>
